/**
 * User-agent → device classification for the sessions list. Pure string
 * parsing (no network): turns the `token_data.user_agent` the auth API records
 * at sign-in into a human device name + which `DeviceIcon` glyph to draw.
 *
 * Deliberately small and heuristic — there is no reliable way to tell a MacBook
 * from an iMac, so we name by platform ("Mac", "Windows PC") and pick the glyph
 * that best fits the form factor. Names are natural-language i18n keys (rendered
 * through <Text>), so they read correctly until a translation is supplied.
 */

/** Which silhouette `DeviceIcon` should draw for a session. */
export type DeviceGlyph = 'laptop' | 'desktop' | 'phone' | 'tablet' | 'generic';

export type DeviceInfo = {
  /** User-visible device name (natural-language i18n key). */
  name: string;
  /** Glyph the `DeviceIcon` component renders. */
  glyph: DeviceGlyph;
  /** User-visible browser name, or null when undetected. */
  browser: string | null;
};

/** Best-effort browser name. Order matters (Edge/Opera masquerade as Chrome). */
const detectBrowser = (ua: string): string | null => {
  if (/\bedg(?:e|a|ios)?\//i.test(ua)) return 'Edge';
  if (/\b(?:opr|opera)\//i.test(ua)) return 'Opera';
  if (/\b(?:firefox|fxios)\//i.test(ua)) return 'Firefox';
  if (/\bsamsungbrowser\//i.test(ua)) return 'Samsung Internet';
  if (/\b(?:chrome|crios|crmo)\//i.test(ua)) return 'Chrome';
  if (/\bsafari\//i.test(ua)) return 'Safari';
  return null;
};

const UNKNOWN: DeviceInfo = { name: 'Unknown device', glyph: 'generic', browser: null };

/** Classify a raw user-agent string into a name + glyph (+ browser). */
export const parseUserAgent = (ua?: string | null): DeviceInfo => {
  if (!ua || !ua.trim()) return UNKNOWN;
  const browser = detectBrowser(ua);

  // Apple mobile first — iPhone/iPad/iPod report their model explicitly.
  if (/\biphone\b/i.test(ua)) return { name: 'iPhone', glyph: 'phone', browser };
  if (/\bipad\b/i.test(ua)) return { name: 'iPad', glyph: 'tablet', browser };
  if (/\bipod\b/i.test(ua)) return { name: 'iPod', glyph: 'phone', browser };

  // Android — "Mobile" token distinguishes a phone from a tablet.
  if (/\bandroid\b/i.test(ua)) {
    return /\bmobile\b/i.test(ua)
      ? { name: 'Android phone', glyph: 'phone', browser }
      : { name: 'Android tablet', glyph: 'tablet', browser };
  }

  // Windows (incl. the rare Windows Phone).
  if (/\bwindows phone\b/i.test(ua)) return { name: 'Windows phone', glyph: 'phone', browser };
  if (/\bwindows nt\b|\bwin(?:64|32)\b/i.test(ua)) return { name: 'Windows PC', glyph: 'desktop', browser };

  // macOS (a laptop is the most common Mac; the glyph errs that way).
  if (/\bmacintosh\b|\bmac os x\b/i.test(ua)) return { name: 'Mac', glyph: 'laptop', browser };

  if (/\bcros\b/i.test(ua)) return { name: 'Chromebook', glyph: 'laptop', browser };
  if (/\blinux\b/i.test(ua)) return { name: 'Linux PC', glyph: 'desktop', browser };

  return { ...UNKNOWN, browser };
};
